import { exportClauseChecklist, IClauseChecklist } from "@/api";
import { Button } from "@/components/shared/buttons/Button";
import { LinkRouter } from "@/components/shared/buttons/LinkRouter";
import { sanitizeFileName } from "@/utils";
import { downloadBlob, XLSX_MIME } from "@/utils/download-blob";
import { Stack, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { DownloadIcon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { EditClauseChecklistDialog } from "../dialogs/EditClauseChecklistDialog";
import { ClauseChecklistTopics } from "./ClauseChecklistTopics";

interface IProps {
    clauseChecklist: IClauseChecklist;
}

export const ClauseChecklistDetailsInfo = ({ clauseChecklist }: IProps) => {
    const [editDialogOpen, setEditDialogOpen] = useState(false);

    const exportMutation = useMutation({
        mutationFn: () => exportClauseChecklist(clauseChecklist.id),
        onSuccess: (blob) => {
            const sanitizedTitle = sanitizeFileName(clauseChecklist.name);
            downloadBlob(
                blob,
                `clause_checklist_${sanitizedTitle}.xlsx`,
                XLSX_MIME
            );
        },
        onError: () => {
            toast.error("Couldn't download checklist. Try again.");
        },
    });

    return (
        <>
            <Stack sx={{ pb: 2 }} gap={2}>
                <Stack
                    direction={"row"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                >
                    <Typography variant="h6">Information</Typography>

                    <Stack direction={"row"} gap={1}>
                        <Button
                            variant="outlined"
                            disableElevation
                            sx={{ borderRadius: 29 }}
                            startIcon={
                                <DownloadIcon
                                    width={"0.9rem"}
                                    height={"0.9rem"}
                                />
                            }
                            disabled={exportMutation.isPending}
                            onClick={() => exportMutation.mutate()}
                        >
                            Export
                        </Button>

                        <Button
                            variant="contained"
                            color="secondary"
                            disableElevation
                            sx={{ borderRadius: 29 }}
                            onClick={() => setEditDialogOpen(true)}
                        >
                            Edit
                        </Button>
                    </Stack>
                </Stack>

                <Stack gap={1}>
                    {clauseChecklist.organizations.length > 0 && (
                        <Stack>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                fontSize={14}
                                fontWeight={500}
                            >
                                Organization
                            </Typography>

                            <LinkRouter
                                to={`/admin/organizations/${clauseChecklist.organizations[0].id}/details`}
                            >
                                <Typography
                                    variant="body2"
                                    fontSize={14}
                                    fontWeight={600}
                                    color="primary"
                                >
                                    {clauseChecklist.organizations[0].name}
                                </Typography>
                            </LinkRouter>
                        </Stack>
                    )}

                    {clauseChecklist.organization_group && (
                        <Stack>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                fontSize={14}
                                fontWeight={500}
                            >
                                Group
                            </Typography>

                            <LinkRouter
                                to={`/admin/organization-groups/${clauseChecklist.organization_group.id}/details`}
                            >
                                <Typography
                                    variant="body2"
                                    fontSize={14}
                                    fontWeight={600}
                                    color="primary"
                                >
                                    {clauseChecklist.organization_group.name}
                                </Typography>
                            </LinkRouter>
                        </Stack>
                    )}

                    <Stack>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            fontSize={14}
                            fontWeight={500}
                        >
                            Description
                        </Typography>
                        <Typography
                            variant="body2"
                            fontSize={14}
                            fontWeight={500}
                        >
                            {clauseChecklist.description}
                        </Typography>
                    </Stack>
                </Stack>
            </Stack>

            <ClauseChecklistTopics clauseChecklist={clauseChecklist} />

            <EditClauseChecklistDialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                clauseChecklist={clauseChecklist}
            />
        </>
    );
};
